// country-profiles.ts
// Shared, dependency-free country profile + formatters used by web, mobile, and backend.
// Only depends on the Intl global (available in Next.js/Node, React Native/Hermes, and Bun).
// See docs/Austrialia-New_Zealand/PLAN.md § "Design principle: a Country Profile, not AU/NZ special cases".

// ============================================
// Types
// ============================================

export type CountryCode = 'US' | 'CA' | 'MX' | 'GB' | 'AU' | 'NZ';

export type DateOrder = 'MDY' | 'DMY';

export type RegionLookupType = 'usState' | 'canadianProvince' | 'mexicanState' | 'auState' | 'nzRegion' | null;

export interface CountryAddressShape {
  regionLabel: string;
  regionLookupType: RegionLookupType;
  postalLabel: string;
  /** Regex source string (not a RegExp instance) — construct with `new RegExp(pattern)` when needed. */
  postalPattern: string;
  hasSuburb: boolean;
  regionRequired: boolean;
  /**
   * Label for the "City" field on an address form. Defaults to "City"
   * everywhere except AU/NZ, where "Suburb" is the term locals actually use
   * (AU/NZ addresses distinguish "suburb" from a broader "city"/"town").
   */
  cityLabel: string;
}

export interface CountryBankTransferShape {
  /** AU/NZ = true; others = generic fields, still allowed but no strict validation. */
  supported: boolean;
  /** AU only. */
  hasBsb: boolean;
  bsbLabel?: string;
  accountNumberLabel: string;
  /** [min, max] inclusive digit count. */
  accountNumberDigits?: [number, number];
  /** Valid total digit counts for an NZ bank-branch-account-suffix number, e.g. [15, 16]. */
  nzAccountDigits?: number[];
}

export interface CountryProfile {
  code: CountryCode;
  name: string;
  locale: string;
  currency: string;
  currencySymbol: string;
  dateOrder: DateOrder;
  /** Display hint only — actual formatting goes through Intl via formatDate(). */
  datePattern: string;
  hour12: boolean;
  defaultTimeZone: string;
  timeZones: string[];
  taxLabel: 'Sales Tax' | 'GST' | 'VAT' | 'GST/HST' | 'IVA';
  defaultTaxRate: number | null;
  pricesIncludeTax: boolean;
  businessIdLabel: string;
  /**
   * Label for a VENDOR's tax-identifier field specifically (new-vendor /
   * vendor detail "Tax ID / EIN" field) — distinct from `businessIdLabel`
   * (the company's OWN business id, e.g. shown on invoices) because NZ's
   * vendor-facing wording ("NZBN / GST number") differs from its own
   * business-id label ("NZBN").
   */
  vendorTaxIdLabel: string;
  /** W2-P9 (G-16): label for the supplier's GST registration number when it is a separate id from the business number (NZ 'GST No.'); null = none, the company's `tax_id_number` is the supplier id. */
  gstNumberLabel: string | null;
  /** W2-P9 (G-16): label for a CUSTOMER's business id (client.business_id): AU 'ABN', NZ 'NZBN', others 'Tax ID'. */
  buyerBusinessIdLabel: string;
  invoiceTitle: 'Invoice' | 'Tax Invoice';
  /**
   * Title of the credit document (G-02). US/CA/MX 'Credit Memo', GB/NZ
   * 'Credit Note', AU 'Adjustment Note'. Product assumption pending
   * licensed-professional confirmation of required document elements.
   */
  creditNoteTitle: 'Credit Memo' | 'Credit Note' | 'Adjustment Note';
  dialCode: string;
  measurementSystem: 'imperial' | 'metric';
  paperSize: 'Letter' | 'A4';
  addressShape: CountryAddressShape;
  bankTransferShape: CountryBankTransferShape;
}

// ============================================
// Profiles
// ============================================

export const COUNTRY_PROFILES: Record<CountryCode, CountryProfile> = {
  US: {
    code: 'US',
    name: 'United States',
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    dateOrder: 'MDY',
    datePattern: 'MM/DD/YYYY',
    hour12: true,
    defaultTimeZone: 'America/New_York',
    timeZones: [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Phoenix',
      'America/Los_Angeles',
      'America/Anchorage',
    ],
    taxLabel: 'Sales Tax',
    defaultTaxRate: null,
    pricesIncludeTax: false,
    businessIdLabel: 'EIN',
    vendorTaxIdLabel: 'Tax ID / EIN',
    gstNumberLabel: null,
    buyerBusinessIdLabel: 'Tax ID',
    invoiceTitle: 'Invoice',
    creditNoteTitle: 'Credit Memo',
    dialCode: '+1',
    measurementSystem: 'imperial',
    paperSize: 'Letter',
    addressShape: {
      regionLabel: 'State',
      regionLookupType: 'usState',
      postalLabel: 'ZIP',
      postalPattern: '^\\d{5}(-\\d{4})?$',
      hasSuburb: false,
      regionRequired: true,
      cityLabel: 'City',
    },
    bankTransferShape: {
      supported: false,
      hasBsb: false,
      accountNumberLabel: 'Account number',
    },
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    locale: 'en-CA',
    currency: 'CAD',
    currencySymbol: '$',
    dateOrder: 'MDY',
    datePattern: 'MM/DD/YYYY',
    hour12: true,
    defaultTimeZone: 'America/Toronto',
    timeZones: [
      'America/St_Johns',
      'America/Halifax',
      'America/Toronto',
      'America/Winnipeg',
      'America/Edmonton',
      'America/Vancouver',
    ],
    taxLabel: 'GST/HST',
    defaultTaxRate: null,
    pricesIncludeTax: false,
    businessIdLabel: 'BN',
    vendorTaxIdLabel: 'Tax ID / EIN',
    gstNumberLabel: null,
    buyerBusinessIdLabel: 'Tax ID',
    invoiceTitle: 'Invoice',
    creditNoteTitle: 'Credit Memo',
    dialCode: '+1',
    measurementSystem: 'metric',
    paperSize: 'Letter',
    addressShape: {
      regionLabel: 'Province',
      regionLookupType: 'canadianProvince',
      postalLabel: 'Postal Code',
      postalPattern: '^[A-Za-z]\\d[A-Za-z] ?\\d[A-Za-z]\\d$',
      hasSuburb: false,
      regionRequired: true,
      cityLabel: 'City',
    },
    bankTransferShape: {
      supported: false,
      hasBsb: false,
      accountNumberLabel: 'Account number',
    },
  },
  MX: {
    code: 'MX',
    name: 'Mexico',
    locale: 'es-MX',
    currency: 'MXN',
    currencySymbol: '$',
    dateOrder: 'DMY',
    datePattern: 'DD/MM/YYYY',
    hour12: false,
    defaultTimeZone: 'America/Mexico_City',
    timeZones: [
      'America/Tijuana',
      'America/Hermosillo',
      'America/Chihuahua',
      'America/Mazatlan',
      'America/Mexico_City',
      'America/Cancun',
    ],
    taxLabel: 'IVA',
    defaultTaxRate: null,
    pricesIncludeTax: false,
    businessIdLabel: 'RFC',
    vendorTaxIdLabel: 'Tax ID / EIN',
    gstNumberLabel: null,
    buyerBusinessIdLabel: 'Tax ID',
    invoiceTitle: 'Invoice',
    creditNoteTitle: 'Credit Memo',
    dialCode: '+52',
    measurementSystem: 'metric',
    paperSize: 'Letter',
    addressShape: {
      regionLabel: 'State',
      regionLookupType: 'mexicanState',
      postalLabel: 'Postal Code',
      postalPattern: '^\\d{5}$',
      hasSuburb: false,
      regionRequired: true,
      cityLabel: 'City',
    },
    bankTransferShape: {
      supported: false,
      hasBsb: false,
      accountNumberLabel: 'Account number',
    },
  },
  GB: {
    code: 'GB',
    name: 'United Kingdom',
    locale: 'en-GB',
    currency: 'GBP',
    currencySymbol: '£',
    dateOrder: 'DMY',
    datePattern: 'DD/MM/YYYY',
    hour12: false,
    defaultTimeZone: 'Europe/London',
    timeZones: ['Europe/London'],
    taxLabel: 'VAT',
    defaultTaxRate: null,
    pricesIncludeTax: false,
    businessIdLabel: 'VAT No.',
    vendorTaxIdLabel: 'Tax ID / EIN',
    gstNumberLabel: null,
    buyerBusinessIdLabel: 'Tax ID',
    invoiceTitle: 'Invoice',
    creditNoteTitle: 'Credit Note',
    dialCode: '+44',
    measurementSystem: 'metric',
    paperSize: 'A4',
    addressShape: {
      regionLabel: 'County',
      regionLookupType: null,
      postalLabel: 'Postcode',
      // Standard (simplified) UK postcode pattern — covers all current formats
      // (A9 9AA, A99 9AA, A9A 9AA, AA9 9AA, AA99 9AA, AA9A 9AA), case-insensitive,
      // optional space. Judgment call: not the exhaustive Royal Mail regex, but
      // matches the vast majority of real postcodes.
      postalPattern: '^[A-Za-z]{1,2}\\d[A-Za-z\\d]? ?\\d[A-Za-z]{2}$',
      hasSuburb: false,
      regionRequired: true,
      cityLabel: 'City',
    },
    bankTransferShape: {
      supported: false,
      hasBsb: false,
      accountNumberLabel: 'Account number',
    },
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    locale: 'en-AU',
    currency: 'AUD',
    currencySymbol: '$',
    dateOrder: 'DMY',
    datePattern: 'DD/MM/YYYY',
    hour12: false,
    defaultTimeZone: 'Australia/Sydney',
    timeZones: [
      'Australia/Sydney',
      'Australia/Melbourne',
      'Australia/Brisbane',
      'Australia/Adelaide',
      'Australia/Perth',
      'Australia/Darwin',
      'Australia/Hobart',
      'Australia/Lord_Howe',
    ],
    taxLabel: 'GST',
    defaultTaxRate: 0.1,
    pricesIncludeTax: false, // Tony 2026-09-16: default OFF; the company opts in
    businessIdLabel: 'ABN',
    vendorTaxIdLabel: 'ABN',
    gstNumberLabel: null,
    buyerBusinessIdLabel: 'ABN',
    invoiceTitle: 'Tax Invoice',
    creditNoteTitle: 'Adjustment Note',
    dialCode: '+61',
    measurementSystem: 'metric',
    paperSize: 'A4',
    addressShape: {
      regionLabel: 'State/Territory',
      regionLookupType: 'auState',
      postalLabel: 'Postcode',
      postalPattern: '^\\d{4}$',
      hasSuburb: true,
      regionRequired: true,
      cityLabel: 'Suburb',
    },
    // BSB is validated as exactly 6 digits (fixed structure per RBA/APCA BSB
    // numbering), but the AU bank account number length is deliberately kept
    // permissive (4-10 digits) since it varies by bank/institution and no
    // single regex is universally correct across all Australian banks.
    bankTransferShape: {
      supported: true,
      hasBsb: true,
      bsbLabel: 'BSB',
      accountNumberLabel: 'Account number',
      accountNumberDigits: [4, 10],
    },
  },
  NZ: {
    code: 'NZ',
    name: 'New Zealand',
    locale: 'en-NZ',
    currency: 'NZD',
    currencySymbol: '$',
    dateOrder: 'DMY',
    datePattern: 'DD/MM/YYYY',
    hour12: false,
    defaultTimeZone: 'Pacific/Auckland',
    timeZones: ['Pacific/Auckland', 'Pacific/Chatham'],
    taxLabel: 'GST',
    defaultTaxRate: 0.15,
    pricesIncludeTax: false, // Tony 2026-09-16: default OFF; the company opts in
    businessIdLabel: 'NZBN',
    vendorTaxIdLabel: 'NZBN / GST number',
    gstNumberLabel: 'GST No.',
    buyerBusinessIdLabel: 'NZBN',
    invoiceTitle: 'Tax Invoice',
    creditNoteTitle: 'Credit Note',
    dialCode: '+64',
    measurementSystem: 'metric',
    paperSize: 'A4',
    addressShape: {
      regionLabel: 'Region',
      regionLookupType: 'nzRegion',
      postalLabel: 'Postcode',
      postalPattern: '^\\d{4}$',
      hasSuburb: true,
      regionRequired: false,
      cityLabel: 'Suburb',
    },
    // NZ bank account numbers follow the standard Payments NZ / IRD structure:
    // bank code (2 digits) + branch/sort code (4 digits) + account number
    // (7 digits) + suffix (2 or 3 digits) = 15 or 16 digits total.
    bankTransferShape: {
      supported: true,
      hasBsb: false,
      accountNumberLabel: 'Account number',
      nzAccountDigits: [15, 16],
    },
  },
};

const DEFAULT_COUNTRY: CountryCode = 'US';

function isCountryCode(value: string): value is CountryCode {
  return Object.prototype.hasOwnProperty.call(COUNTRY_PROFILES, value);
}

/**
 * Resolves a CountryProfile from a raw country code string, an object carrying
 * `countryCode` (e.g. the company API response), or nothing — always falls
 * back to US.
 */
export function resolveCountryProfile(
  input: string | { countryCode?: string | null } | null | undefined
): CountryProfile {
  let raw: string | null | undefined;
  if (typeof input === 'string') {
    raw = input;
  } else if (input && typeof input === 'object') {
    raw = input.countryCode;
  } else {
    raw = undefined;
  }

  const code = (raw ?? '').trim().toUpperCase();
  if (isCountryCode(code)) {
    return COUNTRY_PROFILES[code];
  }
  return COUNTRY_PROFILES[DEFAULT_COUNTRY];
}

// ============================================
// Default time zone per country / AU state
// ============================================

/** What `company.time_zone` holds when nothing set a zone (the column DEFAULT). */
export const COMPANY_TIME_ZONE_COLUMN_DEFAULT = 'America/New_York';

/** AU state/territory code -> IANA zone of its main population centre (ACT shares NSW's zone). */
export const AU_STATE_TIME_ZONES: Record<string, string> = {
  NSW: 'Australia/Sydney',
  ACT: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  TAS: 'Australia/Hobart',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  NT: 'Australia/Darwin',
  WA: 'Australia/Perth',
};

/**
 * The IANA time zone a NEW company in this country should start with. AU is derived from the
 * state/territory code when known (unknown/blank AU state -> the AU profile default, Sydney);
 * every other country uses its profile default (NZ -> Pacific/Auckland, unknown -> US).
 */
export function defaultTimeZoneFor(
  countryCode: string | null | undefined,
  stateCode?: string | null,
): string {
  const profile = resolveCountryProfile(countryCode);
  if (profile.code === 'AU') {
    const st = (stateCode ?? '').trim().toUpperCase();
    return AU_STATE_TIME_ZONES[st] ?? profile.defaultTimeZone;
  }
  return profile.defaultTimeZone;
}

/**
 * Decides whether an existing company.time_zone should be replaced because the company's
 * country was just set (provisioning / warm-pool claim). Returns the zone to write, or null =
 * leave alone. Only AU and NZ are handled (single-zone-ish, unambiguous defaults; US is the
 * column default already; CA/MX/GB keep today's behaviour). ONLY an unset value or the column
 * default ('America/New_York') is ever replaced, so a zone chosen explicitly is never overwritten.
 */
export function resolveTimeZonePatch(
  countryCode: string | null | undefined,
  stateCode: string | null | undefined,
  existingTimeZone: string | null | undefined,
): string | null {
  const profile = resolveCountryProfile(countryCode);
  if (profile.code !== 'AU' && profile.code !== 'NZ') return null;
  const existing = (existingTimeZone ?? '').trim();
  if (existing && existing !== COMPANY_TIME_ZONE_COLUMN_DEFAULT) return null;
  return defaultTimeZoneFor(profile.code, stateCode);
}

function toProfile(profile: CountryProfile | CountryCode | string): CountryProfile {
  if (typeof profile === 'string') {
    return resolveCountryProfile(profile);
  }
  return profile;
}

// ============================================
// Money / number formatting
// ============================================

export interface FormatMoneyOptions {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** When true, appends the ISO currency code, e.g. "AUD". */
  showCode?: boolean;
}

function toNumber(amount: number | string | null | undefined): number {
  if (amount === null || amount === undefined || amount === '') return 0;
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? n : 0;
}

export function formatMoney(
  amount: number | string | null | undefined,
  profile: CountryProfile | CountryCode | string,
  opts: FormatMoneyOptions = {}
): string {
  const p = toProfile(profile);
  const currency = opts.currency ?? p.currency;
  let n = toNumber(amount);

  // An amount that rounds to zero must read "$0.00", never "-$0.00" (negative zero / -0.001 from unapplied-credit netting).
  const shownDigits = opts.maximumFractionDigits ?? opts.minimumFractionDigits ?? 2;
  if (n !== 0 && Math.abs(n) < 0.5 * Math.pow(10, -shownDigits)) n = 0;
  if (Object.is(n, -0)) n = 0;

  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(p.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: opts.minimumFractionDigits,
      maximumFractionDigits: opts.maximumFractionDigits,
    }).format(n);
  } catch {
    // Fall back to a plain number with the currency symbol if the locale/currency
    // combination is somehow unsupported by the runtime's Intl data.
    formatted = `${p.currencySymbol}${n.toFixed(opts.maximumFractionDigits ?? 2)}`;
  }

  return opts.showCode ? `${formatted} ${currency}` : formatted;
}

export interface FormatNumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export function formatNumber(
  n: number | string | null | undefined,
  profile: CountryProfile | CountryCode | string,
  opts: FormatNumberOptions = {}
): string {
  const p = toProfile(profile);
  const value = toNumber(n);
  try {
    return new Intl.NumberFormat(p.locale, opts).format(value);
  } catch {
    return String(value);
  }
}

// ============================================
// Date / time formatting
// ============================================

// A calendar date: plain YYYY-MM-DD, OR the midnight-UTC form that date-only
// columns stored as timestamptz come back as (YYYY-MM-DDT00:00:00Z / +00:00).
// Both must render the same calendar day in every time zone — never shifted.
const DAY_FIRST_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BARE_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?(?:Z|\+00:00))?$/;

/**
 * Parses input into a Date. A bare 'YYYY-MM-DD' string is treated as a
 * calendar date (constructed from local y/m/d components) rather than a UTC
 * instant, so it never shifts a day when formatted in a non-UTC time zone.
 */
function toDate(input: Date | string | number | null | undefined, instant = false): Date | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const bareMatch = BARE_DATE_RE.exec(input.trim());
  if (bareMatch && !(instant && input.trim().length > 10)) {
    const year = Number(bareMatch[1]);
    const month = Number(bareMatch[2]);
    const day = Number(bareMatch[3]);
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface FormatDateOptions {
  style?: 'short' | 'medium' | 'long';
  timeZone?: string;
  /**
   * 'timestamp': a real instant (creAt, modAt) - a UTC-midnight value is converted to the company time zone like
   * any other instant (same rule as shared/date-range-filter.ts). Omitted/'date': legacy behaviour, a
   * UTC-midnight value is a calendar date and never shifts.
   */
  kind?: 'date' | 'timestamp';
}

/** True when `input` is a bare 'YYYY-MM-DD' string (a calendar date, not an instant). */
function isBareDateString(input: Date | string | number | null | undefined, instant = false): boolean {
  if (typeof input !== 'string') return false;
  const v = input.trim();
  return BARE_DATE_RE.test(v) && !(instant && v.length > 10);
}

export function formatDate(
  input: Date | string | number | null | undefined,
  profile: CountryProfile | CountryCode | string,
  opts: FormatDateOptions = {}
): string {
  const p = toProfile(profile);
  const instant = opts.kind === 'timestamp';
  const date = toDate(input, instant);
  if (!date) return '';

  const style = opts.style ?? 'short';
  // A bare calendar-date string (no time-of-day component) must render the
  // same calendar day regardless of time zone — never pass a timeZone for it,
  // since toDate() already built it from local y/m/d components.
  const timeZone = isBareDateString(input, instant) ? undefined : opts.timeZone;

  // Day-first countries (AU/NZ/GB): grids and documents show "01 Jul 2026"
  // (zero-padded day, short month name) for both short and medium styles —
  // ruling Tony 2026-09-15. Numeric dd/mm/yyyy was judged too easy to misread
  // next to US users. US/CA keep numeric short (09/13/2026) and "Sep 13, 2026".
  const dayFirst = p.dateOrder === 'DMY';

  if (dayFirst && p.locale.startsWith('en') && style !== 'long') {
    // Fixed three-letter English months: Intl's en-AU/en-GB "short" month is
    // "June"/"July"/"Sept", which is neither compact nor stable across ICU
    // versions. Resolve the calendar parts in the target zone, then compose.
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric', timeZone,
    }).formatToParts(date);
    const get = (t: string) => parts.find(x => x.type === t)?.value ?? '';
    const mon = DAY_FIRST_MONTHS[Number(get('month')) - 1] ?? get('month');
    return `${get('day').padStart(2, '0')} ${mon} ${get('year')}`;
  }

  if (style === 'short') {
    const dtf = new Intl.DateTimeFormat(p.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    });
    return dtf.format(date);
  }

  const monthStyle = style === 'long' ? 'long' : 'short';
  const dtf = new Intl.DateTimeFormat(p.locale, {
    year: 'numeric',
    month: monthStyle,
    day: dayFirst ? '2-digit' : 'numeric',
    timeZone,
  });
  return dtf.format(date);
}

export interface FormatDateTimeOptions {
  style?: 'short' | 'medium' | 'long';
  timeZone?: string;
  /** See FormatDateOptions.kind. */
  kind?: 'date' | 'timestamp';
}

export function formatDateTime(
  input: Date | string | number | null | undefined,
  profile: CountryProfile | CountryCode | string,
  opts: FormatDateTimeOptions = {}
): string {
  const p = toProfile(profile);
  const instant = opts.kind === 'timestamp';
  const date = toDate(input, instant);
  if (!date) return '';

  const timeZone = isBareDateString(input, instant) ? undefined : opts.timeZone ?? p.defaultTimeZone;
  const style = opts.style ?? 'short';
  const monthStyle = style === 'long' ? 'long' : style === 'medium' ? 'short' : '2-digit';

  const dtf = new Intl.DateTimeFormat(p.locale, {
    year: 'numeric',
    month: monthStyle,
    day: style === 'short' ? '2-digit' : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: p.hour12,
    timeZone,
  });
  return dtf.format(date);
}

export interface FormatTimeOptions {
  timeZone?: string;
}

export function formatTime(
  input: Date | string | number | null | undefined,
  profile: CountryProfile | CountryCode | string,
  opts: FormatTimeOptions = {}
): string {
  const p = toProfile(profile);
  const date = toDate(input);
  if (!date) return '';

  const timeZone = isBareDateString(input) ? undefined : opts.timeZone ?? p.defaultTimeZone;
  const dtf = new Intl.DateTimeFormat(p.locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: p.hour12,
    timeZone,
  });
  return dtf.format(date);
}

// ============================================
// Measurement system (imperial / metric)
// ============================================

export type MeasurementSystem = 'imperial' | 'metric';

export interface UnitLabels {
  system: MeasurementSystem;
  distanceLong: 'Miles' | 'Kilometres';
  distanceShort: 'mi' | 'km';
  fuelEconomyLabel: 'MPG' | 'L/100km';
  volumeLabel: 'gal' | 'L';
  speedLabel: 'mph' | 'km/h';
}

const UNIT_LABELS: Record<MeasurementSystem, UnitLabels> = {
  imperial: {
    system: 'imperial',
    distanceLong: 'Miles',
    distanceShort: 'mi',
    fuelEconomyLabel: 'MPG',
    volumeLabel: 'gal',
    speedLabel: 'mph',
  },
  metric: {
    system: 'metric',
    distanceLong: 'Kilometres',
    distanceShort: 'km',
    fuelEconomyLabel: 'L/100km',
    volumeLabel: 'L',
    speedLabel: 'km/h',
  },
};

/**
 * Resolves the effective measurement system for a company: an explicit
 * `override` ('imperial' | 'metric') always wins; otherwise falls back to
 * what the company's country implies (US = imperial; CA/MX/GB/AU/NZ = metric).
 * A null/undefined/empty/unrecognized override means "follow the country".
 */
export function resolveMeasurementSystem(
  countryCode: string | { countryCode?: string | null } | null | undefined,
  override?: string | null
): MeasurementSystem {
  const normalizedOverride = (override ?? '').trim().toLowerCase();
  if (normalizedOverride === 'imperial' || normalizedOverride === 'metric') {
    return normalizedOverride;
  }
  return resolveCountryProfile(countryCode).measurementSystem;
}

/** Returns the display labels (distance/fuel economy/volume/speed) for a measurement system. */
export function unitLabels(system: MeasurementSystem): UnitLabels {
  return UNIT_LABELS[system] ?? UNIT_LABELS.imperial;
}

/**
 * Formats a distance given in meters as a human string in the given system,
 * e.g. 1609 -> "1.0 mi" (imperial) or "1.6 km" (metric).
 */
export function formatDistanceFromMeters(
  meters: number | null | undefined,
  system: MeasurementSystem,
  opts: { maximumFractionDigits?: number } = {}
): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '';
  const digits = opts.maximumFractionDigits ?? 1;
  const labels = unitLabels(system);
  const value = system === 'imperial' ? meters / 1609.344 : meters / 1000;
  const formatted = value.toFixed(digits);
  return `${formatted} ${labels.distanceShort}`;
}

// ============================================
// Phone
// ============================================

/**
 * Normalizes a raw phone string to E.164 for the given country profile.
 * Pragmatic, not a full libphonenumber replacement — never throws; returns
 * null when it cannot confidently produce a normalized number.
 */
export function normalizePhone(
  raw: string | null | undefined,
  profile: CountryProfile | CountryCode | string
): string | null {
  if (!raw) return null;
  const p = toProfile(profile);
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Already E.164 (or looks like it) — keep the leading '+' and strip the rest.
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : null;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  if (p.code === 'AU' || p.code === 'NZ' || p.code === 'GB') {
    // National trunk prefix '0' is dropped and replaced with the dial code.
    const national = digits.startsWith('0') ? digits.slice(1) : digits;
    return national ? `${p.dialCode}${national}` : null;
  }

  if (p.code === 'US' || p.code === 'CA') {
    if (digits.length === 10) {
      return `${p.dialCode}${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    // Unrecognized length — best effort: prepend dial code as-is.
    return `${p.dialCode}${digits}`;
  }

  if (p.code === 'MX') {
    if (digits.length === 10) {
      return `${p.dialCode}${digits}`;
    }
    return `+${digits}`;
  }

  return `${p.dialCode}${digits}`;
}

/**
 * Formats a raw phone string for national display. Covers the common shapes
 * (US/CA NANP, AU mobile/landline, NZ mobile/landline); falls back to the
 * trimmed input for anything else it doesn't recognize — never throws.
 */
export function formatPhone(raw: string | null | undefined, profile: CountryProfile | CountryCode | string): string {
  if (!raw) return '';
  const p = toProfile(profile);
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Work from digits only, but remember whether a leading '+' / dial code was present.
  let digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) {
    const dialDigits = p.dialCode.slice(1);
    if (digits.startsWith(dialDigits)) {
      digits = digits.slice(dialDigits.length);
    }
  } else if (digits.startsWith('1') && (p.code === 'US' || p.code === 'CA') && digits.length === 11) {
    digits = digits.slice(1);
  }

  if (p.code === 'US' || p.code === 'CA') {
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return trimmed;
  }

  if (p.code === 'AU') {
    // National form always starts with a trunk '0'.
    const national = digits.startsWith('0') ? digits : `0${digits}`;
    if (national.length === 10 && national.startsWith('04')) {
      // Mobile: 04xx xxx xxx
      return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
    }
    if (national.length === 10) {
      // Landline: (0x) xxxx xxxx
      return `(${national.slice(0, 2)}) ${national.slice(2, 6)} ${national.slice(6)}`;
    }
    return trimmed;
  }

  if (p.code === 'NZ') {
    const national = digits.startsWith('0') ? digits : `0${digits}`;
    if (national.startsWith('02')) {
      // Mobile: 02x xxx xxxx (9 digits) or 02x xxxx xxxx (10 digits, less common)
      if (national.length === 9) {
        return `${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
      }
      if (national.length === 10) {
        return `${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
      }
      return trimmed;
    }
    if (national.length === 9) {
      // Landline: (0x) xxx xxxx
      return `(${national.slice(0, 2)}) ${national.slice(2, 5)} ${national.slice(5)}`;
    }
    return trimmed;
  }

  return trimmed;
}

// ============================================
// Bank transfer details (AU/NZ)
// ============================================

export interface BankTransferDetails {
  enabled: boolean;
  showOnInvoices: boolean;
  accountName: string;
  bsb?: string | null;
  accountNumber: string;
  bankName?: string | null;
  paymentInstructions?: string | null;
}

/**
 * Strips all non-digit characters. Returns '' for null/undefined — never throws.
 */
export function normalizeBankDigits(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\D/g, '');
}

/**
 * Validates bank-transfer details against a country's bank-transfer shape.
 * Pragmatic, never throws. When the feature is off (`!details.enabled`),
 * validation is always skipped — this is critical for backward compatibility
 * with existing (non-bank-transfer) invoices.
 */
export function validateBankTransfer(
  details: Partial<BankTransferDetails> | null | undefined,
  profile: CountryProfile | CountryCode | string
): { valid: boolean; errors: string[] } {
  if (!details || !details.enabled) {
    return { valid: true, errors: [] };
  }

  const p = toProfile(profile);
  const shape = p.bankTransferShape;
  const errors: string[] = [];

  if (!details.accountName || !details.accountName.trim()) {
    errors.push('Account name is required');
  }

  if (shape.hasBsb) {
    const bsbDigits = normalizeBankDigits(details.bsb);
    if (!details.bsb || !bsbDigits || bsbDigits.length !== 6) {
      errors.push('BSB must be exactly 6 digits');
    }

    const acctDigits = normalizeBankDigits(details.accountNumber);
    const [min, max] = shape.accountNumberDigits ?? [4, 10];
    if (!details.accountNumber || !acctDigits || acctDigits.length < min || acctDigits.length > max) {
      errors.push(`Account number must be between ${min} and ${max} digits`);
    }
  } else if (shape.nzAccountDigits && shape.nzAccountDigits.length > 0) {
    const acctDigits = normalizeBankDigits(details.accountNumber);
    if (!details.accountNumber || !acctDigits || !shape.nzAccountDigits.includes(acctDigits.length)) {
      errors.push('Account number must be 15 or 16 digits (bank-branch-account-suffix)');
    }
  } else {
    if (!details.accountNumber || !details.accountNumber.trim()) {
      errors.push('Account number is required');
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Formats a bank account number for display per country. Never throws.
 * - AU: this is the ACCOUNT NUMBER (not BSB) — returned as the normalized
 *   digit string, trimmed. No special grouping since AU account-number
 *   lengths vary by bank and there's no universal grouping convention.
 * - NZ: normalizes digits; 15-digit -> "NN-NNNN-NNNNNNN-NN" (2-4-7-2),
 *   16-digit -> "NN-NNNN-NNNNNNN-NNN" (2-4-7-3). Any other length is
 *   returned as the raw trimmed string, unformatted (don't force-format
 *   invalid input).
 * - Unsupported/other countries: raw trimmed string unchanged (no digit
 *   stripping — preserve whatever was entered since format isn't validated).
 */
export function formatBankAccountNumber(
  raw: string | null | undefined,
  profile: CountryProfile | CountryCode | string
): string {
  if (!raw) return '';
  const p = toProfile(profile);
  const shape = p.bankTransferShape;
  const trimmed = raw.trim();

  if (shape.hasBsb) {
    return normalizeBankDigits(raw).trim();
  }

  if (shape.nzAccountDigits && shape.nzAccountDigits.length > 0) {
    const digits = normalizeBankDigits(raw);
    if (digits.length === 15) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 13)}-${digits.slice(13)}`;
    }
    if (digits.length === 16) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 13)}-${digits.slice(13)}`;
    }
    return trimmed;
  }

  return trimmed;
}

/**
 * Formats an AU BSB as "123-456" (3-3 split). Never throws — anything that
 * doesn't normalize to exactly 6 digits is returned as the raw trimmed
 * value, unchanged.
 */
export function formatBsb(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = normalizeBankDigits(raw);
  if (digits.length === 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return raw.trim();
}
